import { MutableRefObject } from 'react';
import jsPDF from 'jspdf';
import { displayNumber, round, unum } from '@oliasoft-open-source/units';
import autoTable, { FontStyle } from 'jspdf-autotable';
import { addChart } from 'views/report/utils/add-chart/add-chart.ts';
import { lineInterval } from 'views/report/utils/style-consts.ts';
import { addTitle } from 'views/report/utils/add-title/add-title.ts';
import { Operation } from 'src/common/interfaces/activity-model';
import { groupBy } from 'lodash';

export const addCostVsDepthPage = async (
  doc: jsPDF,
  padding: number,
  forecastLogoHeight: number,
  chartRef: MutableRefObject<null>,
  simulationsResult: any,
  primaryCurrency: string,
  activityModelOperations: Operation[],
) => {
  doc.addPage();

  addTitle(doc, 'Result vs Depth (Cost)', {
    x: padding,
    y: forecastLogoHeight + padding + lineInterval,
  });

  const chart = await addChart(doc, chartRef);

  const operations = simulationsResult?.operations?.flatMap((operation: any) => [operation, operation]);
  const sections = operations?.map((op: any) => {
    const currentOperation: Operation | undefined = (activityModelOperations ?? []).find(
      (operation) => operation.operationId === op.id,
    );
    return {
      projectSectionId: currentOperation?.projectSectionId ?? null,
      projectSectionName: currentOperation?.projectSectionName ?? op.name ?? '',
    };
  });
  const operationVsDepth = simulationsResult?.operation_cost_vs_depth;
  const tableData: any = { ...operationVsDepth, operations, sections };
  const groupedSEctions = groupBy(
    sections?.filter((s: any) => s.projectSectionId != null),
    'projectSectionId',
  );

  const rows = tableData?.['10']?.depth?.map((el: number, index: number) => {
    const currentSectionId = tableData.sections?.[index]?.projectSectionId;
    const previousSectionId = tableData.sections?.[index - 1]?.projectSectionId;
    const hasSectionData = currentSectionId != null;
    const hadPreviousSectionData = previousSectionId != null;

    const isFirstInSection = hasSectionData && (index === 0 || currentSectionId !== previousSectionId);

    const isFirstWithoutSection = !hasSectionData && (index === 0 || hadPreviousSectionData);

    const rowsWithoutSection = tableData.sections?.slice(index).filter((s: any) => s.projectSectionId == null).length;

    const getSectionCell = () => {
      if (isFirstInSection) {
        return [
          {
            content: tableData?.sections?.[index]?.projectSectionName || '',
            rowSpan: groupedSEctions?.[currentSectionId]?.length ?? 1,
          },
        ];
      }
      if (isFirstWithoutSection) {
        return [
          {
            content: '',
            rowSpan: rowsWithoutSection,
          },
        ];
      }
      return [];
    };

    return [
      ...getSectionCell(),
      ...(index % 2 === 0 ? [{ content: tableData?.operations?.[index]?.name || '', rowSpan: 2 }] : []),
      {
        content: round(unum(el, 'm', 'm'), 0),
        styles: { fontStyle: 'bold' as FontStyle },
      },
      displayNumber(round(tableData?.mean?.costs?.[index], 0), {
        scientific: false,
      }),
      displayNumber(round(tableData?.['10']?.costs?.[index], 0), {
        scientific: false,
      }),
      displayNumber(round(tableData?.['50']?.costs?.[index], 0), {
        scientific: false,
      }),
      displayNumber(round(tableData?.['90']?.costs?.[index], 0), {
        scientific: false,
      }),
    ];
  });

  const tableStartY = padding + forecastLogoHeight + chart.chartHeight + 2 * lineInterval;
  autoTable(doc, {
    head: [
      ['Section', 'Operation', 'Depth', 'Mean', 'P10', 'P50', 'P90'].map((label) => ({
        content: label,
        styles: { fontStyle: 'bold' },
      })),
      ['', '', 'm', primaryCurrency, primaryCurrency, primaryCurrency, primaryCurrency].map((content) => ({
        content,
        styles: { fontStyle: 'italic' },
      })),
    ],
    body: rows,
    startY: tableStartY,
    theme: 'plain',
    styles: {
      fontSize: 10,
      cellPadding: { top: 2, right: 2, bottom: 2, left: 0 },
      halign: 'left',
    },

    didDrawCell: (data) => {
      const { cell, doc } = data;
      const { x, y, width, height } = cell;
      // Draw bottom border for each cell
      doc.setDrawColor('#90908d');
      doc.setLineWidth(0.2);
      doc.line(x, y + height, x + width, y + height);
    },
    margin: { top: 30 },
  });
};
